<script setup>
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesPartyDocumentEditor from './SalesPartyDocumentEditor.vue';

defineProps({
  canEdit: { type: Boolean, default: false },
  documentTypes: { type: Array, default: () => [] },
  documents: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  savingDocumentId: { type: [String, Number], default: null },
  showEmptyState: { type: Boolean, default: false },
});

const emit = defineEmits(['download', 'remove', 'saveTypes']);
const { t } = useI18n();
const saveTypes = (document, types) => emit('saveTypes', document, types);
const formatSize = bytes =>
  bytes
    ? t('SALES_PARTIES.DOCUMENTS.SIZE', {
        value: (Number(bytes) / 1024 / 1024).toFixed(2),
      })
    : t('SALES_PARTIES.COMMON.NO_DATA');
</script>

<template>
  <p v-if="loading" class="text-sm text-lais-text-muted">
    {{ t('SALES_PARTIES.DOCUMENTS.LOADING') }}
  </p>
  <p
    v-else-if="!documents.length && showEmptyState"
    class="text-sm text-lais-text-muted"
  >
    {{ t('SALES_PARTIES.COMMON.NO_DOCUMENT') }}
  </p>
  <ul v-else class="grid gap-2">
    <li
      v-for="document in documents"
      :key="document.id"
      class="grid gap-3 rounded-lais-sm border border-lais-border-soft bg-white p-3"
    >
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <p class="truncate text-sm font-semibold text-lais-text">
            {{ document.filename }}
          </p>
          <p class="mt-1 text-xs text-lais-text-muted">
            {{ formatSize(document.byte_size) }}
          </p>
          <div
            v-if="document.document_types?.length"
            class="mt-2 flex flex-wrap gap-1"
          >
            <span
              v-for="type in document.document_types"
              :key="type.id || type.name"
              class="rounded-full bg-lais-primary-50 px-2 py-0.5 text-[11px] font-semibold text-lais-primary"
            >
              {{ type.name || type.nome || type.id }}
            </span>
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <LaisPrimaryButton
            v-if="document.download_available"
            size="sm"
            variant="ghost"
            icon="i-lucide-download"
            @click="emit('download', document)"
          >
            {{ t('SALES_PARTIES.DOCUMENTS.DOWNLOAD') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            v-if="document.can_delete"
            size="sm"
            variant="ghost"
            icon="i-lucide-trash-2"
            @click="emit('remove', document)"
          >
            {{ t('SALES_PARTIES.DOCUMENTS.REMOVE') }}
          </LaisPrimaryButton>
        </div>
      </div>
      <SalesPartyDocumentEditor
        :can-edit="canEdit"
        :document="document"
        :options="documentTypes"
        :saving="String(savingDocumentId) === String(document.id)"
        @save="saveTypes"
      />
    </li>
  </ul>
</template>
